import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  viewChild,
  input,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { ModalFormComponent } from '@pedix-workspace/angular-ui-modal';

import { Integration } from '@pedix-workspace/integrations-shared';
import {
  InputCheckboxComponent,
  InputRadioGroupComponent,
  InputTextComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { FormsModule } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { IconBillingVoucherComponent } from '@pedix-workspace/angular-ui-icons';
import { CreateVoucherModalPreviewComponent } from './create-voucher-modal-preview/create-voucher-modal-preview.component';
import {
  generateVoucher,
  isArcaGenericError,
  isArcaUnkownError,
  isArcaVoucherDataError,
} from '@pedix-workspace/integrations-arca';
import { EnvironmentService } from '@pedix-workspace/angular-utils';
import { ToastrService } from 'ngx-toastr';
import { EndOrderDetails, TaxVoucherSummary } from '@pedix-workspace/utils';
import { CreateVoucherBatchErrorsComponent } from './create-voucher-batch-errors/create-voucher-batch-errors.component';
import { ApiV1_Establishment } from '@pedix-workspace/api-v1';
import { TaxVoucherActionsService } from '../tax-voucher-actions/tax-voucher-actions.service';

export type BillingMode = 'individual' | 'combined';

export type VoucherCreationQueueItem = {
  orderId: string;
  orderDetails: EndOrderDetails;
  status: 'pending' | 'success' | 'error';
  taxVoucherSummary?: TaxVoucherSummary;
  errors?: string[];
};

@Component({
  selector: 'pxw-create-voucher-modal',
  templateUrl: './create-voucher-modal.component.html',
  styleUrls: ['./create-voucher-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslocoDirective,
    ModalFormComponent,
    InputRadioGroupComponent,
    InputCheckboxComponent,
    InputTextComponent,
    AlertComponent,
    FormsModule,
    ButtonComponent,
    IconBillingVoucherComponent,
    CreateVoucherModalPreviewComponent,
    CreateVoucherBatchErrorsComponent,
  ],
})
export class CreateVoucherModalComponent {
  private environment = inject(EnvironmentService);
  private toastrService = inject(ToastrService);
  private t = inject(TranslocoService);
  private taxVoucherActionsService = inject(TaxVoucherActionsService);

  readonly modalForm = viewChild.required(ModalFormComponent);

  readonly electronicBillingIntegration = input.required<Integration>();
  readonly establishment = input.required<ApiV1_Establishment>();

  readonly selectedOrders = signal<EndOrderDetails[]>([]);
  readonly creatingVouchers = signal<boolean>(false);
  readonly billingMode = signal<BillingMode>('individual');
  readonly mergeConceptsByOrder = signal<boolean>(false);
  readonly customConcept = signal<string>('');
  readonly voucherCreationQueue = signal<VoucherCreationQueueItem[]>([]);

  readonly title = computed(() => {
    return this.selectedOrders().length === 1 ? 'Facturar pedido' : 'Facturar pedidos';
  });
  readonly billingInfoMatchesInEveryOrder = computed(() => {
    const baseBillingInfo = this.selectedOrders()[0].billingInformation;

    return this.selectedOrders().every(order => isEqual(order.billingInformation, baseBillingInfo));
  });
  readonly displayCombinedBillingError = computed(() => {
    return this.billingMode() === 'combined' && !this.billingInfoMatchesInEveryOrder();
  });
  readonly hasErrors = computed(() => {
    return this.displayCombinedBillingError();
  });
  readonly billingModeOptions = computed(() => {
    const options = [{ label: 'Individual (una factura por cada pedido)', value: 'individual' }];
    if (this.selectedOrders().length > 1) {
      options.push({
        label: 'Combinada (una factura incluyendo todos los pedidos)',
        value: 'combined',
      });
    }

    return options;
  });
  readonly numberToVouchersToGenerate = computed(() => {
    if (this.billingMode() === 'combined') {
      return 1;
    }
    return this.selectedOrders().length;
  });

  async open(selectedOrders: EndOrderDetails[]) {
    this.selectedOrders.set(selectedOrders);
    this.voucherCreationQueue.set([]);

    this.modalForm().open();
  }

  async onCreateVouchers(): Promise<void> {
    if (this.numberToVouchersToGenerate() === 1) {
      this.generateSingleVoucher();
    } else {
      this.generateBatchVouchers();
    }
  }

  private async generateSingleVoucher() {
    this.creatingVouchers.set(true);

    const taxVoucherSummary = await this.taxVoucherActionsService.onGenerateTaxVoucher({
      establishmentId: this.establishment().id,
      orderIds: this.selectedOrders().map(orderDetails => orderDetails.id!),
      billingInformation: this.selectedOrders()[0].billingInformation!,
      groupingConceptName: this.customConcept(),
      groupByOrder: this.mergeConceptsByOrder(),
    });

    this.creatingVouchers.set(false);

    if (taxVoucherSummary) {
      this.toastrService.success(
        this.t.translate(
          'Factura ' + taxVoucherSummary.details.voucherRef + ' generada correctamente',
        ),
      );

      this.modalForm().close();
    }
  }

  private async generateBatchVouchers() {
    this.creatingVouchers.set(true);

    this.voucherCreationQueue.set(
      this.selectedOrders().map(orderDetails => ({
        orderId: orderDetails.id!,
        orderDetails,
        status: 'pending',
      })),
    );

    for (const item of this.voucherCreationQueue()) {
      try {
        const { taxVoucherSummary } = await generateVoucher(this.environment.apiIntegrationsUrl, {
          establishmentId: this.establishment().id,
          orderIds: [item.orderId],
          billingInformation: item.orderDetails.billingInformation!,
          groupingConceptName: this.customConcept(),
          groupByOrder: this.mergeConceptsByOrder(),
        });

        this.voucherCreationQueue.update(queueItems => {
          return queueItems.map(currentItem => {
            if (currentItem.orderId === item.orderId) {
              return { ...item, status: 'success', taxVoucherSummary };
            }
            return currentItem;
          });
        });
      } catch (error) {
        this.voucherCreationQueue.update(queueItems => {
          return queueItems.map(currentItem => {
            let errors: string[] = [];

            if (isArcaUnkownError(error)) {
              errors = [error.details.Code + ': ' + error.details.Msg];
            } else if (isArcaVoucherDataError(error)) {
              errors = error.observaciones.map(o => o.code + ': ' + o.message);
            } else if (isArcaGenericError(error)) {
              errors = [error.message];
            } else {
              errors = ['Error desconocido al generar el comprobante'];
            }

            if (currentItem.orderId === item.orderId) {
              return {
                ...item,
                status: 'error',
                errors,
              };
            }
            return currentItem;
          });
        });
      }
    }

    if (this.voucherCreationQueue().every(item => item.status === 'success')) {
      this.toastrService.success('Facturas generadas correctamente');
    } else {
      this.toastrService.warning(
        'Corrija los errores indicados y reintente la creación en los pedidos fallidos.',
        'Proceso completado con errores',
      );
    }

    this.creatingVouchers.set(false);
  }

  onBillingModeChanged(value: BillingMode) {
    if (value === 'individual') {
      this.mergeConceptsByOrder.set(false);
    }
  }
}
